import ResourceList from "@/components/ResourceList";
import ReportDocumentView from "@/components/ReportDocumentView";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const ADMIN_ROLES = ["admin", "control", "planner", "superintendent", "supervisor", "owner"];

// Hoisted so the drawer sees a stable component identity across renders
// (react/no-unstable-nested-components).
const renderBreakdownDoc = (bd) => <ReportDocumentView type="breakdown" item={bd} />;

/**
 * When a breakdown is logged we cascade the SMU (hour meter) reading and any
 * work-order number down to the matching in-flight inspection for the same
 * asset so the supervisor doesn't have to type the hours twice — once on the
 * breakdown, once on the inspection sign-off.
 *
 * Match rule: any inspection for `asset_id` that is NOT yet approved/closed
 * (draft / in_progress / submitted / pending). If several match we update the
 * latest by `started_at`. Silent-fail on network errors so the breakdown
 * create flow itself is never blocked.
 */
async function propagateBreakdownToInspection(bd) {
  if (!bd?.asset_id) return;
  const smu = bd.smu;
  const wo = bd.work_order_number;
  if (!smu && !wo) return;
  try {
    const { data } = await api.get("/inspections", {
      params: { asset_id: bd.asset_id, limit: 20 },
    });
    const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
    const openStates = new Set(["draft", "in_progress", "submitted", "pending", "open"]);
    const candidates = list
      .filter((i) => i.asset_id === bd.asset_id && !i.approved_at && openStates.has(String(i.status || "").toLowerCase()))
      .sort((a, b) => new Date(b.started_at || b.created_at || 0) - new Date(a.started_at || a.created_at || 0));
    if (!candidates.length) return;
    const insp = candidates[0];
    const iid = insp.id || insp._id;
    // Update the WO# via the inspection-level endpoint.
    if (wo && wo !== insp.work_order_number) {
      await api.patch(`/inspections/${iid}`, { work_order_number: String(wo).trim() || null });
    }
    // Find the SMU measurement item and PATCH its value so the sticky-header
    // chip, sign-off panel, and PDF all reflect the breakdown's hour reading.
    if (smu) {
      const num = Number(smu);
      if (Number.isFinite(num)) {
        // Refetch the individual inspection so we see its items_state.
        const full = await api.get(`/inspections/${iid}`);
        const sections = full?.data?.sections || [];
        let smuItem = null;
        for (const sec of sections) {
          for (const it of (sec.items || [])) {
            if (it.kind === "measurement" && /s\.?m\.?u|meter/i.test(it.label || "")) {
              smuItem = it;
              break;
            }
          }
          if (smuItem) break;
        }
        if (smuItem) {
          await api.patch(`/inspections/${iid}/items/${smuItem.id}`, {
            value: num,
            client_op_id: `${smuItem.id}-bd-${Date.now()}`,
            extras: { source: "breakdown-cascade", from_breakdown_id: bd.id || bd._id },
          });
        }
      }
    }
  } catch (ex) {
    // Non-blocking — the breakdown itself was already created successfully.
    console.warn("propagateBreakdownToInspection failed:", ex?.message || ex);
  }
}

export default function Breakdowns() {
  const { user } = useAuth();
  return (
    <ResourceList
      endpoint="/breakdowns"
      title="Asset Breakdowns"
      subtitle="Maintenance"
      resourceName="Breakdown"
      documentStyle
      extraDetail={renderBreakdownDoc}
      detailPath={(it) => `/breakdowns/${it.id || it._id}`}
      itemLabel={(it) => `${it.asset_id || "Asset"} · ${it.complaint || it.cause || "Breakdown"}`}
      itemMeta={(it) => [
        { label: "Asset", value: it.asset_id },
        { label: "Employee", value: it.employee_name },
        { label: "Supervisor", value: it.supervisor_name },
        { label: "Down at", value: fmtDate(it.breakdown_time || it.created_at) },
      ]}
      statusOf={(it) => it.status || (it.approved_at ? "approved" : (it.is_finalised ? "awaiting sign-off" : "open"))}
      priorityOf={(it) => it.priority || it.severity}
      searchFields={["asset_id", "employee_name", "complaint", "cause", "corrective_action"]}
      testid="breakdowns-page"
      emptyLabel="No breakdowns logged."
      signOffConfig={{
        endpoint: (it) => `/breakdowns/${it.id || it._id}/approve`,
        label: "Approve Breakdown",
        verb: "approve",
        requireSignature: true,
        // Hide the sticky "Approve" prompt once the supervisor has signed.
        alreadySigned: (it) => !!it.approved_at || !!it.supervisor_sig || String(it.status || "").toLowerCase() === "approved",
        // API expects the supervisor's signature on approval.
        payload: ({ signature }) => ({
          supervisor_sig: signature,
          supervisor_name: user?.name || user?.username || "Supervisor",
        }),
      }}
      amendConfig={{
        endpoint: (it) => `/breakdowns/${it.id || it._id}/amend`,
      }}
      createConfig={{
        singular: "Breakdown",
        title: "Log New Breakdown",
        roles: ADMIN_ROLES,
        // After the breakdown is logged, cascade its SMU + WO# into the
        // matching in-flight inspection so the same hours don't have to be
        // typed twice.
        onCreated: (bd) => propagateBreakdownToInspection(bd),
        fields: [
          { name: "employee_name", label: "Employee Name *", required: true, placeholder: "e.g. J. Smith" },
          { name: "asset_id", label: "Asset ID *", required: true, placeholder: "CB-A1-001" },
          { name: "breakdown_time", label: "Breakdown Time *", required: true, type: "datetime-local" },
          {
            name: "assigned_supervisor_id",
            label: "Assigned Supervisor *",
            required: true,
            type: "select-async",
            optionsEndpoint: "/admin/users",
            filter: (u) => ["supervisor", "superintendent", "owner"].includes(String(u.role || "").toLowerCase()),
            optionLabel: (u) => `${u.name} · ${(u.role || u.trade || "").toString().toUpperCase()}${u.crew_name ? ` · ${u.crew_name}` : ""}`,
            hint: "Sign-off notification will fire for this supervisor",
          },
          { name: "smu", label: "SMU (hour meter)", hint: "Cascades to the active inspection for this asset" },
          { name: "work_order_number", label: "Work Order #", placeholder: "WO-12345", hint: "Cascades to the active inspection for this asset" },
          { name: "location", label: "Location" },
          { name: "complaint", label: "Failure Description", type: "textarea", rows: 2 },
          { name: "cause", label: "Root Cause", type: "textarea", rows: 2 },
          { name: "corrective_action", label: "Repair Actions", type: "textarea", rows: 3 },
          { name: "back_in_service_time", label: "Back in Service", type: "datetime-local", hint: "When filled, asset auto-flips to RELEASED FOR OPERATIONS" },
          { name: "part_number", label: "Part Number" },
          { name: "part_name", label: "Part Name" },
          { name: "quantity", label: "Quantity" },
          { name: "comments", label: "Comments", type: "textarea", rows: 2 },
          { name: "subcategory", label: "Component Affected" },
          { name: "photos", label: "Photos", type: "photos", max: 8 },
        ],
      }}
    />
  );
}
function fmtDate(v) { if (!v) return null; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
